import { useMemo } from "react";

import { AccountInfo } from "@azure/msal-browser";
/**
 * Permission checking hooks using generated permer code for client-side validation.
 *
 * This module provides usePermerRequirements: Get all generated permission check functions
 *
 * Hook extracts user attributes from MSAL authentication and use the
 * permer_web library for client-side permission checking without server calls.
 */
import { useMsal } from "@azure/msal-react";

import { useGetSecurityPolicyPermerSecurityPolicyGetSuspense } from "@/services/2/react-query/hooks";

import { makePermerRequirements, type PermerRequirements } from "../codegen/permer/requirements";

// Helper function to extract user attributes (shared logic)
const extractUserAttributes = (activeAccount: AccountInfo): string[] => {
  const userAttributes: string[] = [];

  if (activeAccount.idTokenClaims?.job) {
    const job = activeAccount.idTokenClaims.job;
    if (typeof job === "string") {
      const roleAttribute = `role:oh-${job.replaceAll("_", "-")}`;
      userAttributes.push(roleAttribute);
    }
  }

  return [...new Set(userAttributes)];
};

/**
 * Hook that provides access to all generated permer requirements functions.
 * This provides a convenient API for checking multiple permissions.
 *
 * @example
 * const permer = usePermerRequirements();
 * if (permer.hasDsitAdmin()) {
 *   // User can access admin
 * }
 */
export const usePermerRequirements = (): PermerRequirements => {
  const { instance } = useMsal();
  const { data: securityPolicy } = useGetSecurityPolicyPermerSecurityPolicyGetSuspense();

  return useMemo(() => {
    const activeAccount = instance.getActiveAccount();
    if (!activeAccount) throw new Error("No active account found");
    console.debug("Generating permer requirements for active account", activeAccount);
    const userAttributes = extractUserAttributes(activeAccount);
    console.debug("permer user attributes", userAttributes);
    return makePermerRequirements(userAttributes, securityPolicy);
  }, [instance, securityPolicy]);
};
